import { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { Power } from 'lucide-react';
import { ToastType } from '../Toast';

interface AIGenerationToggleProps {
  onShowToast: (type: ToastType, title: string, message?: string) => void;
}

export function AIGenerationToggle({ onShowToast }: AIGenerationToggleProps) {
  const [isEnabled, setIsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  useEffect(() => {
    loadStatus();
  }, []);

  const loadStatus = async () => {
    try {
      const { data, error } = await supabase
        .from('system_config')
        .select('value, updated_at')
        .eq('key', 'ai_generation_enabled')
        .maybeSingle();

      if (error) throw error;

      setIsEnabled(data?.value === 'true');
      setLastUpdated(data?.updated_at || null);
    } catch (error) {
      console.error('Error loading AI generation status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = () => {
    if (isEnabled) {
      setShowConfirmDialog(true);
    } else {
      updateStatus(true);
    }
  };

  const updateStatus = async (newStatus: boolean) => {
    setSaving(true);
    try {
      const { error } = await supabase
        .from('system_config')
        .update({
          value: newStatus ? 'true' : 'false',
          updated_at: new Date().toISOString()
        })
        .eq('key', 'ai_generation_enabled');

      if (error) throw error;

      setIsEnabled(newStatus);
      setLastUpdated(new Date().toISOString());
      setShowConfirmDialog(false);

      onShowToast(
        'success',
        newStatus
          ? 'AI generatie is ingeschakeld voor alle users'
          : 'AI generatie is uitgeschakeld voor alle users'
      );
    } catch (error) {
      console.error('Error updating AI generation status:', error);
      onShowToast('error', 'Er ging iets fout bij het opslaan');
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Onbekend';
    const date = new Date(dateString);
    return date.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
          <div className="h-12 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Power className={`w-5 h-5 ${isEnabled ? 'text-green-600' : 'text-gray-400'}`} />
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                AI Generatie voor Users
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {isEnabled ? 'Users kunnen nieuwe content maken' : 'Nieuwe content is uitgeschakeld'}
              </p>
            </div>
          </div>

          <button
            onClick={handleToggle}
            disabled={saving}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              isEnabled ? 'bg-green-600' : 'bg-gray-300'
            } ${saving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                isEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {showConfirmDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              AI Generatie Uitschakelen?
            </h3>
            <p className="text-gray-600 mb-6">
              Users kunnen dan geen nieuwe blogs of email campaigns aanmaken.
              Bestaande content en geplande campaigns blijven gewoon werken.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirmDialog(false)}
                disabled={saving}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={() => updateStatus(false)}
                disabled={saving}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {saving ? 'Bezig...' : 'Uitschakelen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
